import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight, Linkedin, Instagram, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/data/site";

const columns = [
  {
    title: "Services",
    links: [
      { label: "Website Design", to: "/services" },
      { label: "Local SEO", to: "/services" },
      { label: "Google Ads", to: "/services" },
      { label: "Branding", to: "/services" },
      { label: "Social Media", to: "/services" },
    ],
  },
  {
    title: "Industries",
    links: [
      { label: "Healthcare", to: "/industries" },
      { label: "Restaurants", to: "/industries" },
      { label: "Real Estate", to: "/industries" },
      { label: "Retail", to: "/industries" },
      { label: "Professional Services", to: "/industries" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Portfolio", to: "/portfolio" },
      { label: "Case Studies", to: "/case-studies" },
      { label: "Our Process", to: "/process" },
      { label: "Blog", to: "/blog" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "FAQ", to: "/faq" },
      { label: "Contact", to: "/contact" },
      { label: "Admin", to: "/admin/login" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed! Watch your inbox for growth tips.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative mt-24 overflow-hidden bg-dq-ink text-white">
      <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-dq-orange/20 blur-3xl" aria-hidden="true" />
      <div className="dq-container relative py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_2fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-white font-display text-lg font-extrabold text-dq-ink">D</span>
              <span className="font-display text-xl font-extrabold">DiziTeQ</span>
            </div>
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-white/60">
              Helping local businesses grow with premium digital solutions — websites,
              Local SEO, Google Business Profile, branding & advertising in Bengaluru.
            </p>

            <div className="mt-6 space-y-3 text-sm text-white/70">
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 hover:text-white">
                <Phone className="h-4 w-4 text-dq-orange" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-white">
                <Mail className="h-4 w-4 text-dq-orange" /> {CONTACT.email}
              </a>
              <p className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-dq-orange" /> {CONTACT.city}
              </p>
            </div>

            <div className="mt-6 flex gap-2">
              {[Linkedin, Instagram, Facebook, Twitter].map((Icon, i) => (
                <a key={i} href={CONTACT.website} target="_blank" rel="noreferrer" aria-label="social"
                   className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-dq-orange hover:text-dq-orange">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
              {columns.map((col) => (
                <div key={col.title}>
                  <h4 className="font-display text-sm font-bold text-white">{col.title}</h4>
                  <ul className="mt-4 space-y-2.5">
                    {col.links.map((l) => (
                      <li key={l.label}>
                        <Link to={l.to} className="text-sm text-white/55 transition-colors hover:text-white">
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-6">
              <h4 className="font-display text-base font-bold">Get growth tips in your inbox</h4>
              <p className="mt-1 text-sm text-white/55">Join our newsletter. No spam, unsubscribe anytime.</p>
              <form onSubmit={subscribe} className="mt-4 flex flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  data-testid="newsletter-email"
                  className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm text-white placeholder:text-white/40 focus:border-dq-orange focus:outline-none"
                />
                <button type="submit" disabled={loading} data-testid="newsletter-submit"
                  className="btn-primary shrink-0 disabled:opacity-60">
                  {loading ? "..." : "Subscribe"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-sm text-white/60 sm:flex-row">
          <p>© {new Date().getFullYear()} DiziTeQ Innovative Solutions. All rights reserved.</p>
          <p>Bengaluru&rsquo;s Premium Digital Marketing Agency</p>
        </div>
      </div>
    </footer>
  );
}
